import type { Summary } from './types';
import { getChartRuntime } from './chartRuntime';
import { PIE_PALETTE } from './palettes';
import { buildBarData } from './utils';

export function renderPieChart(summary: Summary): HTMLElement {
  const container = document.createElement('div');
  container.className = 'chart chart--pie';

  const raw = buildBarData(summary);
  if (!raw.length) return container;

  const canvas = document.createElement('canvas');
  container.appendChild(canvas);

  // Yes = high saturation emerald; No = low saturation muted slate
  const POS_COLOR = '#818CF8';
  const NEG_COLOR = '#E2E8F0';
  const norm = (s: string) => s.trim().toLowerCase();
  const classify = (label: string): 'pos' | 'neg' | undefined => {
    const l = norm(label);
    if (l === 'i have' || l === 'yes' || l === 'participated' || l === 'used supports' || l === 'true' || l === 'y') return 'pos';
    if (l === 'i have not' || l === 'no' || l === 'did not participate' || l === 'did not use supports' || l === 'false' || l === 'n') return 'neg';
    return undefined;
  };
  // Ensure consistent slice order for binary pies (positive first, then negative)
  let data = raw;
  if (raw.length === 2) {
    const posIdx = raw.findIndex((d) => classify(d.label) === 'pos');
    const negIdx = raw.findIndex((d) => classify(d.label) === 'neg');
    if (posIdx !== -1 && negIdx !== -1) {
      data = [raw[posIdx], raw[negIdx]];
    }
  }

  const colors = data.map((entry, index) => {
    const cls = classify(entry.label);
    if (cls === 'pos') return POS_COLOR;
    if (cls === 'neg') return NEG_COLOR;
    return PIE_PALETTE[index % PIE_PALETTE.length];
  });
  const ChartRuntime = getChartRuntime();

  const chart = new ChartRuntime(canvas, {
    type: 'pie',
    data: {
      labels: data.map((entry) => entry.label),
      datasets: [
        { data: data.map((entry) => entry.count), backgroundColor: colors, borderWidth: 0 },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 600 },
      plugins: {
        legend: { position: 'bottom', labels: { color: '#475569', boxWidth: 16 } },
        tooltip: {
          callbacks: {
            label(context: any) {
              const count = context.raw as number;
              const total = summary.n || 0;
              const pct = total ? `${((count / total) * 100).toFixed(1)}%` : '0%';
              return `${context.label}: ${count} (${pct})`;
            },
          },
        },
      },
    },
  });

  (globalThis as any).__activeCharts = (globalThis as any).__activeCharts || [];
  (globalThis as any).__activeCharts.push(chart);
  return container;
}

export function shouldRenderPie(questionType: string, summary: Summary, chartHint?: string): boolean {
  const hint = (chartHint || '').trim().toLowerCase();
  if (hint === 'pie' || hint === 'donut' || hint === 'doughnut') return true;
  if (hint && hint.startsWith('bar')) return false;
  if (!summary.labels || !summary.counts) return false;
  const labelCount = summary.labels.length;
  if (labelCount < 3 || labelCount > 8) return false;
  if (questionType !== 'MC') return false;
  return true;
}
